import type { PagePlatform, PageProductInfo, PageType } from '@teo/widget-shared';

export type EvidenceScope = 'page' | 'main-entity' | 'list-item' | 'unknown';
export type EvidenceKind =
  | 'detail-event' | 'list-event' | 'ambiguous-detail' | 'commerce-item-list'
  | 'product-card' | 'structured-product' | 'microdata-product'
  | 'open-graph-product' | 'dom-product' | 'page-hint' | 'page-title';

export type TypedIdentity = { kind: 'sku' | 'item_id' | 'id' | 'product_id'; value: string; origin: string };

export type PageEvidence = {
  evidenceId: string;
  source: string;
  kind: EvidenceKind;
  scope: EvidenceScope;
  epoch: number;
  active: boolean;
  pageUrl?: string;
  entityUrl?: string;
  identity?: TypedIdentity;
  name?: string;
  product?: PageProductInfo;
  title?: string;
  description?: string;
  breadcrumbs?: string[];
  platform?: PagePlatform;
  pageHint?: PageType;
  structuredMetadata?: boolean;
  h1Match?: boolean;
  purchaseAction?: boolean;
  typedIdMatch?: boolean;
  inMain?: boolean;
  inRepeatedCard?: boolean;
  inCarousel?: boolean;
  inItemList?: boolean;
  hidden?: boolean;
};

export type PageDecision = {
  type: PageType;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
  mainEntityId?: string;
  evidenceIds: string[];
  product?: PageProductInfo;
  listItems: PageProductInfo[];
};

export type EvidenceContext = {
  doc: Document;
  url: string;
  pathname: string;
  epoch: number;
  canonicalUrl?: string;
};

export function textValue(value: unknown): string | undefined {
  if (typeof value === 'string') return value.trim() || undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

export function normalizeName(value: string | undefined): string | undefined {
  return value?.replace(/\s+/g, ' ').trim().toLocaleLowerCase() || undefined;
}

export function normalizeEntityUrl(value: string | undefined, pageUrl: string): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value, pageUrl);
    url.hash = '';
    url.pathname = url.pathname.replace(/\/+$/, '') || '/';
    return url.href;
  } catch { return undefined; }
}

export function currentUrlMatches(entityUrl: string | undefined, pageUrl: string): boolean {
  const entity = normalizeEntityUrl(entityUrl, pageUrl);
  const page = normalizeEntityUrl(pageUrl, pageUrl);
  return Boolean(entity && page && entity === page);
}
